// Six-month workforce timeline: people in rows, dates in columns, draggable bars.
// Drag the middle of a bar to move it; drag its edges to change start/finish.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, saveWithOverride, ApiError, currentUser } from '../api';
import { Spinner, ErrorBox, Modal, Field, DateField } from '../ui';
import {
  toAus, todayDarwin, addDays, addMonths, diffDays, eachDate, dowOf,
  STATUS_COLOURS, statusLabel, fullName, ATTENDANCE_STATUSES, MONTHS, paletteColour
} from '../util';

type Drag = {
  id: number;
  mode: 'move' | 'left' | 'right';
  startX: number;
  origIn: string;
  origOut: string;
  delta: number;
  active: boolean;   // true once the pointer has moved far enough to count as a drag
};

// A drag must travel this far before it changes anything. Without it, a few pixels of
// wobble while clicking becomes a saved date change — at monthly zoom one day is only
// 8 pixels wide, so an imprecise click was enough to move someone.
const DRAG_THRESHOLD_PX = 6;

// One reversible action: a single bar move, or a whole bulk shift.
type UndoEntry = {
  label: string;
  changes: { id: number; check_in: string; check_out: string }[];
};

export default function Timeline() {
  const today = todayDarwin();
  const [from, setFrom] = useState(addDays(today, -7));
  const [zoom, setZoom] = useState<number>(18); // px per day: 8 monthly, 18 weekly, 32 daily
  const [people, setPeople] = useState<any[] | null>(null);
  const [periods, setPeriods] = useState<any[]>([]);
  const [leave, setLeave] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [error, setError] = useState<string[] | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState({ company: '', trade: '', status: '', area: '', q: '' });
  const [colourBy, setColourBy] = useState<'status' | 'area'>('area');
  const [projects, setProjects] = useState<any[]>([]);
  const [bulkDays, setBulkDays] = useState('');
  const [editing, setEditing] = useState<any | null>(null);
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  const [undoing, setUndoing] = useState(false);
  const [justDid, setJustDid] = useState<string | null>(null);
  const dragRef = useRef<Drag | null>(null);
  // The drag handler is bound once, so it reads current data through refs rather than
  // stale closure values.
  const periodsRef = useRef<any[]>([]);
  const peopleRef = useRef<any[]>([]);

  const to = addMonths(from, 6);
  const dates = useMemo(() => eachDate(from, to), [from, to]);

  const load = () => {
    setError(null);
    Promise.all([
      api('/people'), api('/attendance'), api('/leave'), api('/companies'), api('/work_areas'), api('/projects')
    ]).then(([p, a, l, c, w, pr]) => {
      setPeople(p); setPeriods(a); setLeave(l); setCompanies(c); setAreas(w); setProjects(pr);
      peopleRef.current = p; periodsRef.current = a;
    }).catch(e => setError([e.message]));
  };
  useEffect(load, []);

  // ---- Undo ----
  // Keeps the previous dates for recent changes so a mistaken drag can be put straight
  // back. In-memory and per session; older changes are recoverable from the change
  // history under Settings.
  const pushUndo = (entry: UndoEntry) => {
    setUndoStack(prev => [entry, ...prev].slice(0, 20));
    setJustDid(entry.label);
  };

  const nameOfPeriod = (periodId: number) => {
    const p = periodsRef.current.find(x => x.id === periodId);
    const person = peopleRef.current.find(x => x.id === p?.person_id);
    return person ? fullName(person) : 'Attendance period';
  };

  const undo = async () => {
    const entry = undoStack[0];
    if (!entry || undoing) return;
    setUndoing(true);
    setError(null);
    try {
      for (const c of entry.changes) {
        await api(`/attendance/${c.id}`, {
          method: 'PUT',
          body: {
            check_in: c.check_in, check_out: c.check_out,
            override_reason: 'Undo of an accidental change on the timeline'
          }
        });
      }
      setUndoStack(prev => prev.slice(1));
      setJustDid(`Undone: ${entry.label}`);
      load();
    } catch (e: any) {
      setError([`Could not undo: ${e.message}`]);
    } finally { setUndoing(false); }
  };

  // Ctrl+Z / Cmd+Z, as long as focus is not in a form field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z') return;
      const el = document.activeElement as HTMLElement | null;
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      e.preventDefault();
      undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undoStack, undoing]);

  // ---- Drag handling ----
  useEffect(() => {
    const move = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = e.clientX - d.startX;
      // Ignore small movements entirely until the pointer clears the threshold, so a
      // click with a little wobble never becomes a date change.
      if (!d.active && Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      const delta = Math.round(dx / zoom);
      if (delta !== d.delta || !d.active) {
        dragRef.current = { ...d, delta, active: true };
        setDrag(dragRef.current);
      }
    };
    const up = async () => {
      const d = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!d || !d.active || d.delta === 0) return;
      let check_in = d.origIn, check_out = d.origOut;
      if (d.mode === 'move') { check_in = addDays(d.origIn, d.delta); check_out = addDays(d.origOut, d.delta); }
      if (d.mode === 'left') check_in = addDays(d.origIn, d.delta);
      if (d.mode === 'right') check_out = addDays(d.origOut, d.delta);
      if (check_out <= check_in) { setError(['Check-out must be after check-in — change not saved.']); return; }
      try {
        await saveWithOverride(`/attendance/${d.id}`, 'PUT', { check_in, check_out });
        const who = nameOfPeriod(d.id);
        pushUndo({
          label: `${who} moved to ${toAus(check_in)}–${toAus(check_out)}`,
          changes: [{ id: d.id, check_in: d.origIn, check_out: d.origOut }]
        });
        load();
      } catch (err: any) {
        if (err instanceof ApiError && err.status === 0) { /* user cancelled override */ }
        else setError([err.message]);
        load();
      }
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [zoom]);

  if (!people) return <Spinner label="Loading timeline…" />;

  const areaName = Object.fromEntries(areas.map(a => [a.id, a.name]));
  const projectName = Object.fromEntries(projects.map(p => [p.id, p.name]));
  const areaIds = areas.map(a => a.id);
  const trades = [...new Set(people.map(p => p.trade).filter(Boolean))].sort();
  const multiProject = projects.length > 1;

  // Colour a bar either by workflow status or by the work area it is assigned to.
  const barColour = (p: any) =>
    colourBy === 'area'
      ? (p.work_area_id ? paletteColour(p.work_area_id, areaIds) : '#8a93a6')
      : (STATUS_COLOURS[p.status] || '#8a93a6');

  const visiblePeople = people.filter(p => {
    if (filters.q && !fullName(p).toLowerCase().includes(filters.q.toLowerCase())) return false;
    if (filters.company && String(p.company_id) !== filters.company) return false;
    if (filters.trade && p.trade !== filters.trade) return false;
    const pp = periods.filter(x => x.person_id === p.id);
    if (filters.status && !pp.some(x => x.status === filters.status)) return false;
    if (filters.area && !pp.some(x => String(x.work_area_id) === filters.area)) return false;
    return pp.length > 0 || p.active; // show active people even with no periods
  });

  const readonly = currentUser?.role === 'readonly' || currentUser?.role === 'supervisor';

  const applyDrag = (p: any) => {
    if (!drag || drag.id !== p.id) return { ci: p.check_in, co: p.check_out };
    let ci = drag.origIn, co = drag.origOut;
    if (drag.mode === 'move') { ci = addDays(ci, drag.delta); co = addDays(co, drag.delta); }
    if (drag.mode === 'left') ci = addDays(ci, drag.delta);
    if (drag.mode === 'right') co = addDays(co, drag.delta);
    return { ci, co };
  };

  const bulkShift = async () => {
    const days = Number(bulkDays);
    if (!Number.isInteger(days) || days === 0 || selected.size === 0) return;
    const reason = window.prompt(`Shift ${selected.size} attendance period(s) by ${days} day(s). Reason for change (recorded in history):`);
    if (reason === null) return;
    // Capture the current dates first so the whole shift can be reversed in one go.
    const before = periods
      .filter(p => selected.has(p.id))
      .map(p => ({ id: p.id, check_in: p.check_in, check_out: p.check_out }));
    try {
      await api('/attendance/bulk-shift', { method: 'POST', body: { ids: [...selected], days, reason } });
      pushUndo({ label: `${before.length} period(s) shifted by ${days} day(s)`, changes: before });
      setSelected(new Set());
      setBulkDays('');
      load();
    } catch (e: any) { setError([e.message]); }
  };

  // Month header segments
  const monthSegs: { label: string; days: number }[] = [];
  for (const d of dates) {
    const label = MONTHS[Number(d.slice(5, 7)) - 1] + ' ' + d.slice(0, 4);
    const last = monthSegs[monthSegs.length - 1];
    if (last && last.label === label) last.days++;
    else monthSegs.push({ label, days: 1 });
  }

  return (
    <div className="page">
      <div className="page-head">
        <h2>Six-month workforce timeline</h2>
        <div className="btn-row">
          <button className="btn" onClick={() => setFrom(addMonths(from, -1))}>← Earlier</button>
          <button className="btn" onClick={() => setFrom(addDays(today, -7))}>Today</button>
          <button className="btn" onClick={() => setFrom(addMonths(from, 1))}>Later →</button>
          <button className="btn" disabled={!undoStack.length || undoing || readonly}
            title={undoStack.length ? `Undo: ${undoStack[0].label}  (Ctrl+Z)` : 'Nothing to undo'}
            onClick={undo}>
            {undoing ? 'Undoing…' : `↶ Undo${undoStack.length > 1 ? ` (${undoStack.length})` : ''}`}
          </button>
          <select className="input" value={zoom} onChange={e => setZoom(Number(e.target.value))}>
            <option value={8}>Monthly zoom</option>
            <option value={18}>Weekly zoom</option>
            <option value={32}>Daily zoom</option>
          </select>
        </div>
      </div>
      <ErrorBox error={error} onDismiss={() => setError(null)} />
      {justDid && (
        <div className="panel no-print" style={{ padding: '8px 12px', borderLeft: '4px solid var(--olive)' }}>
          {justDid}
          {undoStack.length > 0 && !readonly && (
            <button className="btn btn-sm" style={{ marginLeft: 10 }} disabled={undoing} onClick={undo}>
              ↶ Undo
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => setJustDid(null)}>Dismiss</button>
        </div>
      )}

      <div className="timeline">
        <div className="tl-toolbar">
          <input className="input input-search" placeholder="Search person…" value={filters.q}
            onChange={e => setFilters({ ...filters, q: e.target.value })} />
          <select className="input" value={filters.company} onChange={e => setFilters({ ...filters, company: e.target.value })}>
            <option value="">All companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className="input" value={filters.trade} onChange={e => setFilters({ ...filters, trade: e.target.value })}>
            <option value="">All trades</option>
            {trades.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className="input" value={filters.area} onChange={e => setFilters({ ...filters, area: e.target.value })}>
            <option value="">All work areas</option>
            {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select className="input" value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All statuses</option>
            {ATTENDANCE_STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
          </select>
          <select className="input" value={colourBy} onChange={e => setColourBy(e.target.value as any)}
            title="Choose whether bar colour shows the work area or the booking status">
            <option value="area">Colour by work area</option>
            <option value="status">Colour by status</option>
          </select>
          {selected.size > 0 && !readonly && (
            <>
              <span className="muted">{selected.size} selected</span>
              <input className="input" style={{ width: 90 }} placeholder="± days" value={bulkDays}
                onChange={e => setBulkDays(e.target.value)} />
              <button className="btn btn-primary btn-sm" onClick={bulkShift}>Shift dates</button>
              <button className="btn btn-sm" onClick={() => setSelected(new Set())}>Clear</button>
            </>
          )}
        </div>

        <div className="tl-scroll">
          <div className="tl-grid" style={{ width: 190 + dates.length * zoom }}>
            {/* Month header */}
            <div className="tl-header-row">
              <div className="tl-namecol" />
              {monthSegs.map((m, i) => (
                <div key={i} className="tl-daycell" style={{ width: m.days * zoom, fontWeight: 700, fontSize: 11, textAlign: 'left', paddingLeft: 4 }}>
                  {m.label}
                </div>
              ))}
            </div>
            {/* Day header */}
            <div className="tl-header-row2">
              <div className="tl-namecol muted">Person</div>
              {dates.map(d => (
                <div key={d} className="tl-daycell" style={{ width: zoom }}>
                  {zoom >= 18 ? d.slice(8, 10) : (d.slice(8, 10) === '01' ? d.slice(8, 10) : '')}
                </div>
              ))}
            </div>

            {visiblePeople.map(person => {
              const rows = periods.filter(x => x.person_id === person.id);
              const leaves = leave.filter(l => l.person_id === person.id && l.approval_status !== 'declined');
              return (
                <div key={person.id} className="tl-personrow">
                  <div className="tl-namecol"
                    title={[fullName(person), person.role, person.trade].filter(Boolean).join(' — ')}>
                    {fullName(person)}
                    {(person.role || person.trade) &&
                      <span className="muted"> · {person.role || person.trade}</span>}
                  </div>
                  {/* background day cells */}
                  {dates.map((d, i) => (
                    <div key={d}
                      className={'tl-rowbg' + (dowOf(d) === 0 || dowOf(d) === 6 ? ' weekend' : '') + (d === today ? ' today' : '')}
                      style={{ left: 190 + i * zoom, width: zoom }} />
                  ))}
                  {/* attendance bars */}
                  {rows.map(p => {
                    const { ci, co } = applyDrag(p);
                    if (co < from || ci > to) return null;
                    const startIdx = Math.max(0, diffDays(from, ci));
                    const len = Math.max(1, diffDays(ci < from ? from : ci, co > to ? to : co) + 1);
                    const colour = barColour(p);
                    const area = areaName[p.work_area_id] || '';
                    const barWidth = len * zoom - 2;
                    // Show the work area on the bar when it fits, otherwise fall back to dates.
                    const label = barWidth > 70 && area ? area
                      : barWidth > 60 ? `${toAus(ci).slice(0, 5)}–${toAus(co).slice(0, 5)}`
                      : '';
                    return (
                      <div key={p.id}
                        className={'tl-bar' + (selected.has(p.id) ? ' selected' : '')}
                        style={{ left: 190 + startIdx * zoom, width: barWidth, background: colour }}
                        title={[
                          fullName(person),
                          `${toAus(ci)} → ${toAus(co)}  (${statusLabel(p.status)})`,
                          area ? `Work area: ${area}` : 'Work area: not assigned',
                          multiProject ? `Project: ${projectName[p.project_id] || '—'}` : '',
                          p.supervisor ? `Supervisor: ${p.supervisor}` : '',
                          '',
                          'Drag to move, drag edges to resize.',
                          'Ctrl+click to select, double-click to edit.'
                        ].filter(Boolean).join('\n')}
                        onMouseDown={e => {
                          if (readonly || e.button !== 0) return;
                          e.preventDefault();
                          if (e.ctrlKey || e.metaKey) {
                            const next = new Set(selected);
                            next.has(p.id) ? next.delete(p.id) : next.add(p.id);
                            setSelected(next);
                            return;
                          }
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          const rel = e.clientX - rect.left;
                          const mode = rel < 8 ? 'left' : rel > rect.width - 8 ? 'right' : 'move';
                          dragRef.current = { id: p.id, mode, startX: e.clientX, origIn: p.check_in, origOut: p.check_out, delta: 0, active: false };
                          setDrag(dragRef.current);
                        }}
                        onDoubleClick={() => setEditing({ ...p, person_name: fullName(person) })}>
                        {!readonly && <div className="tl-handle left" />}
                        {label}
                        {!readonly && <div className="tl-handle right" />}
                      </div>
                    );
                  })}
                  {/* leave bars */}
                  {leaves.map(l => {
                    if (l.end_date < from || l.start_date > to) return null;
                    const startIdx = Math.max(0, diffDays(from, l.start_date));
                    const len = Math.max(1, diffDays(l.start_date < from ? from : l.start_date, l.end_date > to ? to : l.end_date) + 1);
                    return (
                      <div key={'l' + l.id} className="tl-bar leave-bar"
                        style={{ left: 190 + startIdx * zoom, width: len * zoom - 2, background: STATUS_COLOURS.on_leave }}
                        title={`${l.leave_type} (${l.approval_status})\n${toAus(l.start_date)} → ${toAus(l.end_date)}`} />
                    );
                  })}
                </div>
              );
            })}
            {visiblePeople.length === 0 && (
              <div style={{ padding: 30, color: 'var(--muted)' }}>No people match the filters. Add personnel on the Personnel page.</div>
            )}
          </div>
        </div>
      </div>
      <p className="muted" style={{ marginTop: 8 }}>
        {colourBy === 'area' ? 'Work areas: ' : 'Statuses: '}
        {colourBy === 'area' ? (
          <>
            {areas.map(a => (
              <span key={a.id} style={{ marginRight: 12 }}>
                <span style={{ display: 'inline-block', width: 10, height: 10, background: paletteColour(a.id, areaIds), borderRadius: 2, marginRight: 4 }} />
                {a.name}
              </span>
            ))}
            <span style={{ marginRight: 12 }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, background: '#8a93a6', borderRadius: 2, marginRight: 4 }} />
              not assigned
            </span>
          </>
        ) : ATTENDANCE_STATUSES.map(s => (
          <span key={s} style={{ marginRight: 12 }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, background: STATUS_COLOURS[s], borderRadius: 2, marginRight: 4 }} />
            {statusLabel(s)}
          </span>
        ))}
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: STATUS_COLOURS.on_leave, borderRadius: 2, marginRight: 4 }} />leave</span>
      </p>

      {editing && <QuickEdit period={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function QuickEdit({ period, onClose, onSaved }: { period: any; onClose: () => void; onSaved: () => void }) {
  const [v, setV] = useState({ ...period });
  const [errors, setErrors] = useState<string[]>([]);
  const save = async () => {
    try {
      await saveWithOverride(`/attendance/${period.id}`, 'PUT', {
        check_in: v.check_in, check_out: v.check_out, status: v.status,
        first_work: v.first_work, last_work: v.last_work, notes: v.notes
      });
      onSaved();
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 0) return;
      setErrors(e instanceof ApiError && e.errors.length ? e.errors : [e.message]);
    }
  };
  return (
    <Modal title={`${period.person_name} — attendance #${period.id}`} onClose={onClose}>
      <ErrorBox error={errors} onDismiss={() => setErrors([])} />
      <div className="form-grid">
        <Field label="Check-in"><DateField value={v.check_in} onChange={x => setV({ ...v, check_in: x })} /></Field>
        <Field label="Check-out"><DateField value={v.check_out} onChange={x => setV({ ...v, check_out: x })} /></Field>
        <Field label="First work day"><DateField value={v.first_work} onChange={x => setV({ ...v, first_work: x })} /></Field>
        <Field label="Last work day"><DateField value={v.last_work} onChange={x => setV({ ...v, last_work: x })} /></Field>
        <Field label="Status">
          <select className="input" value={v.status} onChange={e => setV({ ...v, status: e.target.value })}>
            {ATTENDANCE_STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
          </select>
        </Field>
        <Field label="Notes"><input className="input" value={v.notes ?? ''} onChange={e => setV({ ...v, notes: e.target.value })} /></Field>
      </div>
      <div className="btn-row form-actions">
        <button className="btn btn-primary" onClick={save}>Save</button>
        <button className="btn" onClick={onClose}>Cancel</button>
        <span className="muted">Full editing on the <a href="#/attendance">Attendance</a> page.</span>
      </div>
    </Modal>
  );
}
